"use client";

import React, { HTMLAttributes, ReactNode, useMemo } from "react";
import dynamic from "next/dynamic";
import { useInView } from "react-intersection-observer";

interface GenericLazyLoaderProps<T extends Record<string, any> = Record<string, any>> {
  /**
   * The import function for the component to lazy load
   * Example: () => import("./tour-highlights-section")
   */
  importComponent: () => Promise<{ default: React.ComponentType<T> }>;

  /**
   * Props to pass to the lazy-loaded component
   */
  componentProps: T;

  /**
   * Intersection observer configuration
   */
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;

  /**
   * Loading state configuration
   */
  showLoadingSpinner?: boolean;
  loadingMinHeight?: number;
  customLoadingComponent?: ReactNode;

  /**
   * Debug logging
   */
  debugName?: string;

  /**
   * Optional props for the always-rendered wrapper div.
   * Use when a stable anchor/id is needed before lazy content loads.
   */
  wrapperProps?: HTMLAttributes<HTMLDivElement>;
}

/**
 * Generic lazy loader that can be used for any component
 * Uses intersection observer to load components when they come into view
 */
function GenericLazyLoader<T extends Record<string, any> = Record<string, any>>({
  importComponent,
  componentProps,
  threshold = 0.1,
  rootMargin = "200px 0px",
  triggerOnce = true,
  showLoadingSpinner = true,
  loadingMinHeight = 400,
  customLoadingComponent,
  debugName = "Generic Component",
  wrapperProps
}: GenericLazyLoaderProps<T>) {
  const { ref, inView } = useInView({
    threshold,
    rootMargin,
    triggerOnce
  });

  // Debug logging
  console.log(`${debugName} - inView:`, inView);

  // Memoize the lazy component to prevent recreation on every render
  const LazyComponent = useMemo(() => 
     dynamic(importComponent, {
      ssr: false
     }), [importComponent, customLoadingComponent, showLoadingSpinner, loadingMinHeight]);

  return (
    <div ref={ref} {...wrapperProps}>
      {inView && <LazyComponent {...componentProps} />}
    </div>
  );
}

export default GenericLazyLoader;